import Controller from '@ember/controller';

import { task } from 'ember-concurrency';

import { set } from '@ember/object';
import { tracked, cached } from '@glimmer/tracking';
import { inject as service } from '@ember/service';

/**
 * True only for a 404 answer. Ember Data's `NotFoundError` inherits `name`
 * 'Error' (it is built with `Error.call(this, message)`), so the status is read
 * from the error itself or from the JSON:API `errors[0]`, never from `name`.
 */
export function isNotFoundError(error) {
  const status = parseInt(error?.status ?? error?.errors?.[0]?.status, 10);
  return status === 404;
}

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 *      keep in sync with the code):
 *   role:      lib — base controller carrying the checkout's "who is buying"
 *              concern: how the buyer contact is resolved (the logged contact, an
 *              explicit override, or a fresh record) and how it is refreshed once
 *              per page entry.
 *   extends:   Controller (@ember/controller)
 *   template:  none
 *   exports:   isNotFoundError(error), CheckoutContactController (default)
 *   key:       contact getter/setter (+ the `_wrapContact` override slot),
 *              contactStateAlbergo / isAlbergo (the gate that lets a hotel buy on
 *              behalf of its guests), refreshContactTask.
 *   uses:      session (me, reloadMeTask), store, notify, intl, log
 *   siblings:  controllers/checkout.js extends this; routes/checkout.js clears
 *              `_wrapContact` on entry and then performs refreshContactTask
 *   tests:     checkout-getters-test.js
 *   gotchas:   the `contact` getter must stay side-effect free — refreshContactTask's
 *              own comment explains why the reload had to move out of it, and why the
 *              refresh goes through `contacts/me` rather than `contacts/:id`.
 *
 * Split out of controllers/checkout.js: the rest of the wizard (steps, participants,
 * order confirmation) does not need to know how the buyer record is obtained, and
 * this concern carries reasoning that is easy to undo by accident.
 */
export default class CheckoutContactController extends Controller {
  @service intl;
  @service log;
  @service notify;
  @service session;
  @service store;

  /**
   * Explicit buyer override: set by the hotel/"altri" flow and by
   * refreshContactTask when the logged contact turns out to be gone, cleared by
   * routes/checkout.js on every entry.
   */
  @tracked _wrapContact;

  get contactStateAlbergo() {
    return this.session?._snapshotFindById?.('contact_states', '116') ?? null;
  }

  get isAlbergo() {
    const contactStateAlbergoId = this.contactStateAlbergo?.id;
    const contentMeContactStateId = this.session.me?.contact_state?.id;

    return contactStateAlbergoId && contactStateAlbergoId === contentMeContactStateId;
  }

  @cached
  get contact() {
    if (this._wrapContact) {
      return this._wrapContact;
    }

    const contentMeId = this.session.me?.id;
    const isAlbergo = this.isAlbergo;
    const store = this.store;

    if (contentMeId && !isAlbergo) {
      const contact = store.peekRecord('contact', contentMeId);

      if (contact) {
        return contact;
      }
    }

    return store.createRecord('contact');
  }

  set contact(value) {
    set(this, '_wrapContact', value);
  }

  /**
   * Refreshes the logged user's own contact once per checkout entry: a peeked
   * contact carries empty sync hasManys and the form reads `addresses[0]`.
   *
   * Was a bare `contact.reload()` inside the `contact` getter
   * (EMBER-SCUOLE-ECOMMERCE-2YQ) — a network side effect re-fired on every
   * recompute whose rejection nobody consumed, so a `GET /contacts/:id` 404
   * reached Sentry unhandled (Ember rethrows it from the run loop's timer).
   *
   * It now goes through `contacts/me`, not `contacts/:id`, and that is not
   * cosmetic: the API answers `contacts/:id` with the SAME 404 when the contact
   * is gone and when a client role reads an id the ownership gate refuses
   * (POP-151, api_beebeeboard `src/utility/finds.ts:68` — indistinguishable on
   * purpose), so a 404 there cannot be acted on at all. `contacts/me` is scoped
   * to the caller, so its 404 means one thing: the logged contact no longer
   * exists — only then drop the dead reference and hand the user a blank form.
   * Every other failure (5xx, network, parse) still surfaces; the session
   * service swallows the 401 expiry itself.
   */
  @task({ drop: true }) *refreshContactTask() {
    if (this._wrapContact || this.isAlbergo) {
      return;
    }

    const contentMeId = this.session.me?.id;
    const peeked = contentMeId ? this.store.peekRecord('contact', contentMeId) : null;

    // Never refresh over unsaved edits: `reloadMeTask` refetches `me` and the
    // pushed payload wins over what the customer has typed (the same hazard
    // payment-request.js guards after a refused save). A dirty record is by
    // definition not the stale one this refresh exists for.
    if (!peeked || peeked.hasDirtyAttributes) {
      return;
    }

    try {
      yield this.session.reloadMeTask.perform();
    } catch (e) {
      if (!isNotFoundError(e)) {
        this.log.error('checkout refreshContactTask: contact refresh failed', e);
        this.notify.error(this.intl.t('comunication error'));
        return;
      }

      if (this.isDestroyed || this.isDestroying) {
        return;
      }

      // The logged contact is gone server-side: keep the checkout usable on a
      // fresh record instead of pointing the form (and the following PATCH) at
      // a dead id. `session.me` is left alone on purpose — the whole shell
      // renders from it, and unloading it would take the page down.
      set(this, '_wrapContact', this.store.createRecord('contact'));
      this.notify.error(this.intl.t('checkout contact not found'));
    }
  }
}
